"use server";

import type {
  City,
  CityDetailed,
  Country,
  State,
  StateDetailed,
} from "../types/index";
import db from "../utils/prisma";

export async function getAllCountries(): Promise<Country[]> {
  return await db.country.findMany({
    orderBy: { name: "asc" },
    select: {
      code: true,
      name: true,
      iso3: true,
      flagEmoji: true,
    },
  });
}

export async function getStatesOfCountry(
  countryCode: string
): Promise<State[]> {
  if (!countryCode) return [];

  return await db.state.findMany({
    where: { countryCode },
    orderBy: { name: "asc" },
    select: {
      id: true,
      name: true,
      iso2: true,
      iso3: true,
    },
  });
}

export async function getCitiesOfCountryAndState(
  countryCode: string,
  stateId?: number | null
): Promise<City[]> {
  if (!countryCode) return [];

  return await db.city.findMany({
    where: {
      countryCode,
      ...(stateId && { stateId: parseInt(stateId.toString()) }),
    },
    orderBy: { name: "asc" },
    select: {
      id: true,
      name: true,
    },
  });
}

export async function getCountryByCode(
  countryCode: string
): Promise<Country | null> {
  if (!countryCode) return null;

  return await db.country.findUnique({
    where: { code: countryCode },
    select: {
      code: true,
      name: true,
      iso3: true,
      flagEmoji: true,
    },
  });
}

export async function getStateById(
  stateId: number
): Promise<StateDetailed | null> {
  if (!stateId) return null;

  return await db.state.findUnique({
    where: { id: parseInt(stateId.toString()) },
    select: {
      id: true,
      name: true,
      iso2: true,
      iso3: true,
      country: {
        select: {
          code: true,
          name: true,
        },
      },
    },
  });
}

export async function getCityById(cityId: number): Promise<City | null> {
  if (!cityId) return null;

  return await db.city.findUnique({
    where: { id: parseInt(cityId.toString()) },
    select: {
      id: true,
      name: true,
      country: {
        select: {
          code: true,
          name: true,
        },
      },
      state: {
        select: {
          id: true,
          name: true,
        },
      },
    },
  });
}

export async function findStateAndCountryFromCity(
  cityName: string
): Promise<CityDetailed[]> {
  if (!cityName) return [];

  const cities = await db.city.findMany({
    where: {
      name: {
        equals: cityName.toLowerCase(),
      },
    },
    select: {
      id: true,
      name: true,
      country: {
        select: {
          code: true,
          name: true,
        },
      },
      state: {
        select: {
          id: true,
          name: true,
        },
      },
    },
    orderBy: {
      countryCode: "asc",
    },
  });

  return cities.map((city: CityDetailed) => ({
    id: city.id,
    name: city.name,
    country: city.country || null,
    state: city?.state || null,
  }));
}

export async function findCountryFromState(
  stateName: string
): Promise<StateDetailed[]> {
  if (!stateName) return [];

  const states = await db.state.findMany({
    where: {
      name: {
        equals: stateName.toLowerCase(),
      },
    },
    select: {
      id: true,
      name: true,
      country: {
        select: {
          code: true,
          name: true,
        },
      },
    },
    orderBy: {
      countryCode: "asc",
    },
  });

  return states.map((state: StateDetailed) => ({
    id: state.id,
    name: state.name,
    country: state.country || null,
  }));
}
